import { useMemo } from "react";
import { Button, Drawer } from "@heroui/react";

const isIOS = () => /iPad|iPhone|iPod/i.test(navigator.userAgent);
const isAndroid = () => /Android/i.test(navigator.userAgent);

function buildMapLinks(record, mode = "search") {
  const loc = record?.location || {};
  const placeId = loc.place_id || "";
  const name = loc.name || "";
  const lat = typeof loc.lat === "number" ? loc.lat : null;
  const lng = typeof loc.lng === "number" ? loc.lng : null;

  const label = encodeURIComponent(name);
  const pidParam = placeId ? encodeURIComponent(placeId) : "";

  let gmapsWeb, appleWeb, wazeWeb;
  if (mode === "directions") {
    gmapsWeb =
      lat != null && lng != null
        ? `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}${pidParam ? `&destination_place_id=${pidParam}` : ""}`
        : `https://www.google.com/maps/dir/?api=1&destination=${label}${pidParam ? `&destination_place_id=${pidParam}` : ""}`;
    appleWeb =
      lat != null && lng != null
        ? `https://maps.apple.com/?daddr=${lat},${lng}&q=${label}`
        : `https://maps.apple.com/?daddr=${label}`;
    wazeWeb = lat != null && lng != null ? `https://waze.com/ul?ll=${lat},${lng}&navigate=yes` : null;
  } else {
    gmapsWeb =
      lat != null && lng != null
        ? `https://www.google.com/maps/search/?api=1&query=${lat},${lng}${pidParam ? `&query_place_id=${pidParam}` : ""}`
        : `https://www.google.com/maps/search/?api=1&query=${label}${pidParam ? `&query_place_id=${pidParam}` : ""}`;
    appleWeb =
      lat != null && lng != null
        ? `https://maps.apple.com/?q=${label}&ll=${lat},${lng}`
        : `https://maps.apple.com/?q=${label}`;
    wazeWeb = lat != null && lng != null ? `https://waze.com/ul?ll=${lat},${lng}&navigate=yes` : null;
  }

  let appleApp, gmapsApp, wazeApp;
  if (mode === "directions") {
    appleApp = lat != null && lng != null ? `maps://?daddr=${lat},${lng}&dirflg=d` : `maps://?daddr=${label}`;
    gmapsApp =
      lat != null && lng != null
        ? `comgooglemaps://?daddr=${lat},${lng}&directionsmode=driving`
        : `comgooglemaps://?daddr=${label}`;
    wazeApp = lat != null && lng != null ? `waze://?ll=${lat},${lng}&navigate=yes` : null;
  } else {
    appleApp = lat != null && lng != null ? `maps://?q=${label}&ll=${lat},${lng}` : `maps://?q=${label}`;
    gmapsApp =
      lat != null && lng != null ? `comgooglemaps://?q=${label}&center=${lat},${lng}` : `comgooglemaps://?q=${label}`;
    wazeApp = lat != null && lng != null ? `waze://?ll=${lat},${lng}&navigate=yes` : null;
  }

  let gmapsIntent, wazeIntent, geoChooser;
  if (mode === "directions") {
    gmapsIntent =
      lat != null && lng != null
        ? `intent://maps.google.com/maps?daddr=${lat},${lng}(${label})#Intent;scheme=https;package=com.google.android.apps.maps;end`
        : `intent://maps.google.com/maps?daddr=${label}#Intent;scheme=https;package=com.google.android.apps.maps;end`;
    wazeIntent =
      lat != null && lng != null
        ? `intent://?ll=${lat},${lng}&navigate=yes#Intent;scheme=waze;package=com.waze;end`
        : null;
    geoChooser = lat != null && lng != null ? `geo:${lat},${lng}?q=${lat},${lng}(${label})` : null;
  } else {
    gmapsIntent =
      lat != null && lng != null
        ? `intent://maps.google.com/maps?q=${lat},${lng}(${label})#Intent;scheme=https;package=com.google.android.apps.maps;end`
        : `intent://maps.google.com/maps?q=${label}#Intent;scheme=https;package=com.google.android.apps.maps;end`;
    wazeIntent =
      lat != null && lng != null
        ? `intent://?ll=${lat},${lng}&navigate=yes#Intent;scheme=waze;package=com.waze;end`
        : null;
    geoChooser = lat != null && lng != null ? `geo:${lat},${lng}?q=${lat},${lng}(${label})` : null;
  }

  return {
    hasCoords: lat != null && lng != null,
    web: { gmapsWeb, appleWeb, wazeWeb },
    ios: { appleApp, gmapsApp, wazeApp },
    android: { geoChooser, gmapsIntent, wazeIntent },
  };
}

function useOpenTarget(record, mode = "search") {
  const links = useMemo(() => buildMapLinks(record, mode), [record, mode]);

  return (target) => {
    let wentBackground = false;
    const onVis = () => {
      if (document.visibilityState === "hidden") wentBackground = true;
    };
    document.addEventListener("visibilitychange", onVis, { once: true });

    const startFallback = (url) => {
      const t = setTimeout(() => {
        if (!wentBackground && url) window.location.href = url;
        document.removeEventListener("visibilitychange", onVis);
      }, 900);
      const killer = setTimeout(() => {
        clearTimeout(t);
        document.removeEventListener("visibilitychange", onVis);
      }, 2500);
      return () => {
        clearTimeout(t);
        clearTimeout(killer);
        document.removeEventListener("visibilitychange", onVis);
      };
    };

    if (isIOS()) {
      if (target === "apple") {
        const stop = startFallback(links.web.appleWeb);
        window.location.href = links.ios.appleApp;
        return stop;
      }
      if (target === "google") {
        const stop = startFallback(links.web.gmapsWeb);
        window.location.href = links.ios.gmapsApp;
        return stop;
      }
      if (target === "waze") {
        const stop = startFallback(links.web.wazeWeb || links.web.gmapsWeb);
        window.location.href = links.ios.wazeApp || links.web.gmapsWeb;
        return stop;
      }
    } else if (isAndroid()) {
      if (target === "chooser-android") {
        window.location.href = links.android.geoChooser || links.web.gmapsWeb;
        return;
      }
      if (target === "google") {
        const stop = startFallback(links.web.gmapsWeb);
        window.location.href = links.android.gmapsIntent;
        return stop;
      }
      if (target === "waze") {
        const stop = startFallback(links.web.wazeWeb || links.web.gmapsWeb);
        window.location.href = links.android.wazeIntent || links.web.gmapsWeb;
        return stop;
      }
      if (target === "apple") {
        window.location.href = links.android.geoChooser || links.web.gmapsWeb;
        return;
      }
    }

    window.open(links.web.gmapsWeb, "_blank");
  };
}

const OpenInMapsDrawer = ({ location, state, mode = "search" }) => {
  // buildMapLinks/useOpenTarget expect a `record` shaped as { location }, to
  // stay identical to LocationMapsChooser's logic (which reads record.location).
  const record = useMemo(() => ({ location }), [location]);
  const openTarget = useOpenTarget(record, mode);

  if (!location) return null;

  return (
    <Drawer state={state}>
      <Drawer.Backdrop variant="blur" className="bg-black/30">
        <Drawer.Content placement="bottom">
          <Drawer.Dialog className="rounded-t-2xl dark:bg-black pb-10">
            <Drawer.Handle />
            <Drawer.Header className="flex items-start gap-3">
              <Drawer.Heading className="flex flex-col gap-3">
                <div className="flex flex-col text-4xl font-black leading-[38px]">
                  <span className="text-foreground text-[22px] dark:text-white">Open in maps</span>
                </div>

                <div className="flex flex-col gap-1">
                  <span className="text-xs text-muted">Your Location</span>
                  <span className="text-xs text-foreground font-medium truncate max-w-[280px]">{location.name}</span>
                </div>
              </Drawer.Heading>
            </Drawer.Header>

            <Drawer.Body className="pb-4 flex flex-col relative z-10">
              <Button
                size="md"
                variant="outline"
                fullWidth
                className="justify-start gap-3 rounded-t-2xl rounded-b-none h-10"
                onPress={() => {
                  state.close();
                  openTarget(isAndroid() ? "chooser-android" : "apple");
                }}
              >
                <img src="/apple-maps-logo.png" width={26} /> Maps
              </Button>

              <Button
                size="md"
                variant="outline"
                fullWidth
                className="justify-start gap-3 rounded-none h-10 border-y-0"
                onPress={() => {
                  state.close();
                  openTarget("google");
                }}
              >
                <img src="/google-maps-logo.png" width={18} /> Google Maps
              </Button>

              <Button
                size="md"
                variant="outline"
                fullWidth
                className="justify-start gap-3 rounded-b-2xl rounded-t-none h-10"
                onPress={() => {
                  state.close();
                  openTarget("waze");
                }}
              >
                <img src="/waze-logo.png" width={26} /> Waze
              </Button>
            </Drawer.Body>

            <Drawer.Footer className="relative z-10">
              <Button onPress={state.close} className="w-full bg-background text-foreground">
                Cancel
              </Button>
            </Drawer.Footer>

            <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-40 drawer-blob" />
          </Drawer.Dialog>
        </Drawer.Content>
      </Drawer.Backdrop>
    </Drawer>
  );
};

export default OpenInMapsDrawer;